/**
 * Form Type Definitions
 * Centralized form type system for the IT platform
 * Migrado desde winclap-tech-form (26 formularios)
 *
 * NOTE: For Slack channels, priority logic, and SLA calculations,
 * see form-config.ts
 */

export type FormType =
  // Hardware (3 formularios)
  | 'hardware-request'
  | 'hardware-bug'
  | 'hardware-sale'

  // User Management (3 formularios)
  | 'user-onboarding'
  | 'user-offboarding'
  | 'user-management'

  // Departmental (9 formularios)
  | 'access'
  | 'sales'
  | 'strategy'
  | 'finance'
  | 'content'
  | 'marketing'
  | 'people'
  | 'owned-media';

export interface FormCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  forms: FormDefinition[];
}

export interface FormDefinition {
  id: FormType;
  name: string;
  description: string;
  assignedTo: string; // Email del responsable
  category: string;
  priority: 'low' | 'medium' | 'high';
  requiredFields: string[];
  isActive: boolean;
}

/**
 * Form Assignments - Type Definition Context
 *
 * IMPORTANT: This duplicates form-config.ts assignments intentionally.
 * - form-config.ts: Runtime logic and routing (primary source)
 * - form-types.ts: Type definitions and metadata (this file)
 *
 * If you update assignments, update BOTH files.
 */
// Asignaciones automáticas por formulario
export const FORM_ASSIGNMENTS: Record<FormType, string> = {
  // Hardware Forms
  'hardware-request': 'danilo.bergoglio@winclap.com',
  'hardware-bug': 'danilo.bergoglio@winclap.com',
  'hardware-sale': 'danilo.bergoglio@winclap.com',

  // User Management
  'user-onboarding': 'danilo.bergoglio@winclap.com',
  'user-offboarding': 'danilo.bergoglio@winclap.com',
  'user-management': 'danilo.bergoglio@winclap.com',

  // Departmental
  'access': 'franco.saldano@winclap.com',
  'sales': 'franco.saldano@winclap.com',
  'strategy': 'franco.saldano@winclap.com',
  'finance': 'franco.saldano@winclap.com',
  'content': 'danilo.bergoglio@winclap.com',
  'marketing': 'franco.saldano@winclap.com',
  'people': 'franco.saldano@winclap.com',
  'owned-media': 'franco.saldano@winclap.com',
};

// Categorías de formularios
export const FORM_CATEGORIES: FormCategory[] = [
  {
    id: 'hardware',
    name: 'Hardware',
    description: 'Gestión de equipos y dispositivos',
    icon: 'Laptop',
    forms: [
      {
        id: 'hardware-request',
        name: 'Solicitud de Hardware',
        description: 'Solicitar nuevo equipo o dispositivo',
        assignedTo: FORM_ASSIGNMENTS['hardware-request'],
        category: 'hardware',
        priority: 'high',
        requiredFields: ['summary', 'team', 'role', 'changeReason', 'priority'],
        isActive: true,
      },
      {
        id: 'hardware-bug',
        name: 'Error de Hardware',
        description: 'Reportar problema con equipo existente',
        assignedTo: FORM_ASSIGNMENTS['hardware-bug'],
        category: 'hardware',
        priority: 'high',
        requiredFields: ['summary', 'description'],
        isActive: true,
      },
      {
        id: 'hardware-sale',
        name: 'Venta de Hardware',
        description: 'Gestionar venta de equipos usados',
        assignedTo: FORM_ASSIGNMENTS['hardware-sale'],
        category: 'hardware',
        priority: 'low',
        requiredFields: ['summary'],
        isActive: true,
      },
    ],
  },
  {
    id: 'users',
    name: 'Gestión de Usuarios',
    description: 'Altas, bajas y modificaciones de usuarios',
    icon: 'Users',
    forms: [
      {
        id: 'user-onboarding',
        name: 'Alta de Usuario',
        description: 'Incorporar nuevo colaborador',
        assignedTo: FORM_ASSIGNMENTS['user-onboarding'],
        category: 'users',
        priority: 'high',
        requiredFields: [
          'firstName',
          'lastName',
          'clapbase',
          'area',
          'contractType',
          'position',
          'phone',
          'identification',
          'startDate',
        ],
        isActive: true,
      },
      {
        id: 'user-offboarding',
        name: 'Baja de Usuario',
        description: 'Dar de baja colaborador',
        assignedTo: FORM_ASSIGNMENTS['user-offboarding'],
        category: 'users',
        priority: 'high',
        requiredFields: ['summary', 'userEmail'],
        isActive: true,
      },
      {
        id: 'user-management',
        name: 'Gestión de Usuario',
        description: 'Modificaciones o permisos de usuario existente',
        assignedTo: FORM_ASSIGNMENTS['user-management'],
        category: 'users',
        priority: 'medium',
        requiredFields: ['summary'],
        isActive: true,
      },
    ],
  },
  {
    id: 'departmental',
    name: 'Departamentos',
    description: 'Solicitudes por departamento',
    icon: 'Building2',
    forms: [
      {
        id: 'access',
        name: 'Accesos',
        description: 'Solicitar permisos o accesos a sistemas',
        assignedTo: FORM_ASSIGNMENTS['access'],
        category: 'departmental',
        priority: 'high',
        requiredFields: ['summary', 'description'],
        isActive: true,
      },
      {
        id: 'sales',
        name: 'Growth Partners Request',
        description: 'Solicitudes del equipo de ventas',
        assignedTo: FORM_ASSIGNMENTS['sales'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'strategy',
        name: 'Strategy Request',
        description: 'Solicitudes del equipo de estrategia',
        assignedTo: FORM_ASSIGNMENTS['strategy'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'finance',
        name: 'Finance Request',
        description: 'Solicitudes del equipo de finanzas',
        assignedTo: FORM_ASSIGNMENTS['finance'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'content',
        name: 'Content Request',
        description: 'Solicitudes del equipo de contenido',
        assignedTo: FORM_ASSIGNMENTS['content'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'marketing',
        name: 'Marketing Request',
        description: 'Solicitudes del equipo de marketing',
        assignedTo: FORM_ASSIGNMENTS['marketing'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'people',
        name: 'People Request',
        description: 'Solicitudes del equipo de personas',
        assignedTo: FORM_ASSIGNMENTS['people'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
      {
        id: 'owned-media',
        name: 'Owned Media Request',
        description: 'Solicitudes de medios propios',
        assignedTo: FORM_ASSIGNMENTS['owned-media'],
        category: 'departmental',
        priority: 'medium',
        requiredFields: ['summary', 'reason'],
        isActive: true,
      },
    ],
  },
];

// Helper para obtener form definition por ID
export function getFormDefinition(formType: FormType): FormDefinition | undefined {
  for (const category of FORM_CATEGORIES) {
    const form = category.forms.find((f) => f.id === formType);
    if (form) return form;
  }
  return undefined;
}

// Helper para obtener categoría de un formulario
export function getFormCategory(formType: FormType): FormCategory | undefined {
  return FORM_CATEGORIES.find((cat) =>
    cat.forms.some((f) => f.id === formType)
  );
}
